'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, ExternalLink, MessageCircle, RefreshCw, AlertCircle } from 'lucide-react';

// =============================================================================
// ChatPanel — the conversational core
// =============================================================================
//
// Self-contained chat surface. Used by both the floating widget
// ({@link ChatWidget}) and inline chat blocks. Renders the message
// log, an input row, and any suggested-content cards CN8's RAG
// analysis surfaces.
//
// Streaming
// ---------
//
// CN8 returns OpenAI-compatible SSE chunks. We fetch with POST (so
// not EventSource — EventSource doesn't support POST) and read the
// ReadableStream manually. Each `data: { ... }` line carries either:
//
//   - choices[0].delta.content      → next token
//   - usage / conversation_key      → arrives in the final chunk
//   - "[DONE]"                      → terminator
//
// We append tokens to an in-flight assistant message until [DONE],
// then finalise.
//
// Conversation continuity
// -----------------------
//
// conversation_id (cv_*) is stored in localStorage keyed by
// `chatbot:${tenantSlug}:${chatbotId ?? 'default'}:conversation`.
// On mount we read the key and rehydrate via the conversation
// endpoint; on every send we include it so CN8 threads turns
// together.
//
// Theming
// -------
//
// All colours come from CSS vars set by the tenant's theme tokens —
// no hardcoded hex. The panel inherits the surrounding shell's look
// automatically.

export interface ChatPanelProps {
  tenantSlug: string;
  /** Platform chatbot PK; null/undefined → use tenant default. */
  chatbotId?: number | null;
  /** Initial greeting shown before the first user turn. Optional —
   *  the bot's configured greeting can be used. */
  greeting?: string;
  /** Layout style — affects max-height and overall sizing. */
  layout?: 'floating' | 'inline';
  /** Called when the panel determines the chatbot is unavailable
   *  (412/403/404 on first attempt). The floating widget uses this
   *  to hide itself; an inline block uses this to render a
   *  fallback message. */
  onUnavailable?: () => void;
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  /** Only on assistant messages — populated from
   *  enhanced_analysis.relevance_scores when present. */
  suggestions?: Suggestion[];
  pending?: boolean;
  error?: string;
}

interface Suggestion {
  title: string;
  url: string;
  summary?: string;
  score: number;
}

const RELEVANCE_THRESHOLD = 0.3;
const MAX_SUGGESTIONS = 3;

export function ChatPanel({
  tenantSlug,
  chatbotId,
  greeting,
  layout = 'floating',
  onUnavailable,
}: ChatPanelProps) {
  const { t } = useTranslation('site-builder-runtime');
  const [messages, setMessages] = useState<Message[]>(() =>
    greeting ? [{ id: 'greeting', role: 'assistant', content: greeting }] : [],
  );
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const storageKey = useMemo(
    () => `chatbot:${tenantSlug}:${chatbotId ?? 'default'}:conversation`,
    [tenantSlug, chatbotId],
  );

  // -------------------------------------------------------------------------
  // Rehydrate stored conversation on mount
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const cv = window.localStorage.getItem(storageKey);
    if (!cv) return;
    setConversationId(cv);

    fetch(
      `/api/v1/chatbot/conversation/${encodeURIComponent(cv)}?tenantSlug=${encodeURIComponent(tenantSlug)}`,
    )
      .then((r) => {
        if (!r.ok) {
          window.localStorage.removeItem(storageKey);
          return null;
        }
        return r.json();
      })
      .then((detail) => {
        if (!detail || !Array.isArray(detail.messages)) return;
        setMessages([
          ...(greeting ? [{ id: 'greeting', role: 'assistant' as const, content: greeting }] : []),
          ...detail.messages.map((m: { role: string; content: string }, i: number) => ({
            id: `rehyd-${i}`,
            role: m.role as Message['role'],
            content: m.content,
          })),
        ]);
      })
      .catch(() => {
        /* swallow — fresh start is fine */
      });
  }, [storageKey, tenantSlug, greeting]);

  // Auto-scroll to bottom when messages change.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // -------------------------------------------------------------------------
  // Send a message
  // -------------------------------------------------------------------------

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;

    const userMsgId = `u-${Date.now()}`;
    const assistantMsgId = `a-${Date.now()}`;
    const isFirstTurn = messages.length === 0;

    setDraft('');
    setSending(true);
    setMessages((prev) => [
      ...prev,
      { id: userMsgId, role: 'user', content: text },
      { id: assistantMsgId, role: 'assistant', content: '', pending: true },
    ]);

    try {
      const res = await fetch('/api/v1/chatbot/chat/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({
          tenantSlug,
          chatbotId: chatbotId ?? null,
          message: text,
          conversationId,
        }),
      });

      if (!res.ok) {
        // 412 → tenant not provisioned. 403 → bot disabled. 404 → no default.
        if ([404, 403, 412].includes(res.status) && isFirstTurn) {
          onUnavailable?.();
        }
        throw new Error(
          (await readErrorBody(res)) ?? t('chat.errors.failed', { status: res.status }),
        );
      }
      if (!res.body) throw new Error(t('chat.errors.streamingUnsupported'));

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assembled = '';
      let suggestions: Suggestion[] = [];
      let conversationKey: string | null = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by blank lines.
        let frameEnd = buffer.indexOf('\n\n');
        while (frameEnd !== -1) {
          const frame = buffer.slice(0, frameEnd);
          buffer = buffer.slice(frameEnd + 2);
          frameEnd = buffer.indexOf('\n\n');

          const dataLines = frame
            .split('\n')
            .filter((l) => l.startsWith('data:'))
            .map((l) => l.slice(5).trim());

          for (const data of dataLines) {
            if (data === '[DONE]') continue;
            try {
              const json = JSON.parse(data);
              const token = json?.choices?.[0]?.delta?.content;
              if (typeof token === 'string') {
                assembled += token;
                setMessages((prev) =>
                  prev.map((m) => (m.id === assistantMsgId ? { ...m, content: assembled } : m)),
                );
              }
              if (json?.conversation_key) conversationKey = json.conversation_key;
              if (json?.enhanced_analysis?.relevance_scores) {
                suggestions = extractSuggestions(json.enhanced_analysis.relevance_scores);
              }
            } catch {
              /* swallow — heartbeats / non-JSON chunks */
            }
          }
        }
      }

      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? { ...m, pending: false, suggestions: suggestions.length ? suggestions : undefined }
            : m,
        ),
      );

      if (conversationKey) {
        setConversationId(conversationKey);
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(storageKey, conversationKey);
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setMessages((prev) =>
        prev.map((m) => (m.id === assistantMsgId ? { ...m, pending: false, error: msg } : m)),
      );
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const isInline = layout === 'inline';
  return (
    <div
      className="flex flex-col h-full w-full"
      style={{
        background: 'var(--color-surface-card, #fff)',
        color: 'var(--color-text-primary, #111)',
        borderRadius: isInline ? 'var(--radius-md, 12px)' : undefined,
        border: isInline ? '1px solid var(--color-border-default, #e5e7eb)' : undefined,
      }}
    >
      {/* Header */}
      {!isInline && (
        <div
          className="px-4 py-3 border-b flex items-center gap-2"
          style={{ borderColor: 'var(--color-border-default, #e5e7eb)' }}
        >
          <MessageCircle className="h-4 w-4" style={{ color: 'var(--color-brand-500, #6366f1)' }} />
          <div className="text-sm font-semibold">{t('chat.title')}</div>
        </div>
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
        style={{ minHeight: isInline ? 320 : undefined }}
      >
        {messages.length === 0 && (
          <div className="text-sm opacity-60 text-center py-8">{t('chat.empty')}</div>
        )}
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
      </div>

      {/* Input */}
      <div
        className="px-3 py-2 border-t flex items-end gap-2"
        style={{ borderColor: 'var(--color-border-default, #e5e7eb)' }}
      >
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('chat.inputPlaceholder')}
          rows={1}
          disabled={sending}
          className="flex-1 resize-none rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 disabled:opacity-50"
          style={{
            borderColor: 'var(--color-border-default, #e5e7eb)',
            background: 'var(--color-surface-default, #fff)',
            color: 'var(--color-text-primary, #111)',
            maxHeight: 120,
          }}
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={sending || !draft.trim()}
          className="rounded-md p-2 transition-colors disabled:opacity-40"
          style={{
            background: 'var(--color-brand-500, #6366f1)',
            color: 'var(--color-brand-50, #fff)',
          }}
          aria-label={t('actions.send')}
        >
          {sending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// MessageBubble
// =============================================================================

function MessageBubble({ message }: { message: Message }) {
  const { t } = useTranslation('site-builder-runtime');
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className="max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap"
        style={
          isUser
            ? {
                background: 'var(--color-brand-100, #eef2ff)',
                color: 'var(--color-text-primary, #111)',
              }
            : {
                background: 'var(--color-surface-default, #f9fafb)',
                color: 'var(--color-text-primary, #111)',
                border: '1px solid var(--color-border-subtle, #e5e7eb)',
              }
        }
      >
        {message.error ? (
          <span
            className="flex items-start gap-1.5"
            style={{ color: 'var(--color-status-error, #b91c1c)' }}
          >
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>{message.error}</span>
          </span>
        ) : (
          <>
            {message.content || (message.pending ? '…' : '')}
            {message.suggestions && message.suggestions.length > 0 && (
              <div
                className="mt-3 space-y-1.5 border-t pt-2"
                style={{ borderColor: 'var(--color-border-subtle, #e5e7eb)' }}
              >
                <div className="text-xs font-medium opacity-70">{t('chat.suggested')}</div>
                {message.suggestions.map((s, i) => (
                  <a
                    key={i}
                    href={s.url}
                    className="flex items-center gap-1.5 text-xs hover:underline"
                    style={{ color: 'var(--color-brand-600, #4f46e5)' }}
                  >
                    <ExternalLink className="h-3 w-3 shrink-0" />
                    <span className="truncate">{s.title}</span>
                  </a>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function extractSuggestions(
  scores: Array<{
    title?: string;
    url?: string;
    summary?: string;
    relevance_score?: number;
  }>,
): Suggestion[] {
  return scores
    .filter((s) => (s.relevance_score ?? 0) >= RELEVANCE_THRESHOLD && s.url && s.title)
    .slice(0, MAX_SUGGESTIONS)
    .map((s) => ({
      title: s.title!,
      url: s.url!,
      summary: s.summary,
      score: s.relevance_score ?? 0,
    }));
}

/** Backend-supplied failure text, or {@code null} when the response carries
 *  none — the caller then renders the catalogue's generic chat-failed copy. */
async function readErrorBody(res: Response): Promise<string | null> {
  try {
    const txt = await res.text();
    if (!txt) return null;
    try {
      const json = JSON.parse(txt);
      return json.message || json.error || txt;
    } catch {
      return txt;
    }
  } catch {
    return null;
  }
}
